// ── MonthlyActivityPage ("Monthly Activity") — monthly pivots + funnel conversion KPIs ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { Card, ErrorMsg, SectionHeader, Spinner } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";
import { PivotMatrix, KpiCard, pctLabel, parseDate, isExcludedOwner, OwnerMultiSelect } from "./analyticsShared";

export const MonthlyActivityPage = () => {
  const { api } = useApp();
  const [opps, setOpps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [created, setCreated] = useState({ from: "", to: "" });
  const [company, setCompany] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);   // Set multi-select; null = default (all except excluded four)
  const [stageSel, setStageSel] = useState("");
  const [empSel, setEmpSel] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.get("getOpportunities"), api.get("getLeads")])
      .then(([o, l]) => { setOpps(Array.isArray(o) ? o : []); setLeads(Array.isArray(l) ? l : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const owners = useMemo(() => [...new Set([...opps, ...leads].map(o => o.ownerName).filter(Boolean))].sort(), [opps, leads]);
  const sources = useMemo(() => [...new Set([...opps, ...leads].map(o => o.lead_source).filter(Boolean))].sort(), [opps, leads]);
  const stages = useMemo(() => [...new Set(opps.map(o => o.stage).filter(Boolean))].sort(), [opps]);

  // default Sourced By to every owner except the excluded four
  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  const fromD = created.from ? new Date(created.from) : null;
  const toD = created.to ? new Date(created.to) : null;
  const inCreated = (v) => {
    if (!fromD && !toD) return true;
    const d = parseDate(v); if (!d) return false;
    if (fromD && d < fromD) return false;
    if (toD && d > toD) return false;
    return true;
  };

  const oppsF = useMemo(() => {
    const c = company.trim().toLowerCase();
    return opps.filter(o => {
      if (!ownerOk(o.ownerName)) return false;
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (sourceSel && o.lead_source !== sourceSel) return false;
      if (stageSel && o.stage !== stageSel) return false;
      if (!inCreated(o.createdat)) return false;
      return true;
    });
  }, [opps, company, sourceSel, ownerSel, stageSel, created]);

  const leadsF = useMemo(() => {
    const c = company.trim().toLowerCase();
    return leads.filter(l => {
      if (!ownerOk(l.ownerName)) return false;
      if (c && !((l.companyname || "").toLowerCase().includes(c))) return false;
      if (sourceSel && l.lead_source !== sourceSel) return false;
      if (!inCreated(l.createdat)) return false;
      return true;
    });
  }, [leads, company, sourceSel, ownerSel, created]);

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  // empSel highlights the matrices (which keep every employee) but filters the KPI cards
  const oppsSel = empSel ? oppsF.filter(o => (o.ownerName || "—") === empSel) : oppsF;
  const leadsSel = empSel ? leadsF.filter(l => (l.ownerName || "—") === empSel) : leadsF;
  const issuedAll = oppsF.filter(o => o.term_sheet_issued_date);   // matrices (all employees)
  const signedAll = oppsF.filter(o => o.term_sheet_signed_date);
  const issued = oppsSel.filter(o => o.term_sheet_issued_date);    // KPIs (selected)
  const signed = oppsSel.filter(o => o.term_sheet_signed_date);
  const funded = oppsSel.filter(o => o.is_won);
  const convertedLeads = leadsSel.filter(l => l.converted_at || l.converted_accountid);
  const pick = (k) => setEmpSel(prev => prev === k ? "" : k);

  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };

  return <>
    <SectionHeader title="Monthly Activity" />

    {/* slicers — two stacked cards */}
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 150 }} />
        <select value={sourceSel} onChange={e => setSourceSel(e.target.value)} style={selStyle}><option value="">All Lead Sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
        <select value={stageSel} onChange={e => setStageSel(e.target.value)} style={selStyle}><option value="">All Stages</option>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select>
        {empSel && <button onClick={() => setEmpSel("")} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 12, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer" }}>{empSel} <span style={{ fontWeight: 700 }}>✕</span></button>}
      </Card>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
          Opp Created
          <input type="date" value={created.from} onChange={e => setCreated({ ...created, from: e.target.value })} style={selStyle} />
          <span>–</span>
          <input type="date" value={created.to} onChange={e => setCreated({ ...created, to: e.target.value })} style={selStyle} />
        </label>
      </Card>
    </div>

    {/* conversion KPIs */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
      <KpiCard label="Lead → Opp Conversion" value={pctLabel(convertedLeads.length, leadsSel.length)} sub={convertedLeads.length + " of " + leadsSel.length + " leads"} />
      <KpiCard label="Opp → Term Sheet Issued" value={pctLabel(issued.length, oppsSel.length)} sub={issued.length + " of " + oppsSel.length + " opps"} />
      <KpiCard label="TS Issued → TS Signed" value={pctLabel(signed.length, issued.length)} sub={signed.length + " of " + issued.length + " issued"} />
      <KpiCard label="TS Signed → Funded" value={pctLabel(funded.length, signed.length)} sub={funded.length + " of " + signed.length + " signed"} />
    </div>

    <PivotMatrix title="Leads (by created month)" items={leadsF} rowKeyOf={l => l.ownerName} dateOf={l => l.createdat} onRowClick={pick} activeRow={empSel} />
    <PivotMatrix title="Opportunities (by created month)" items={oppsF} rowKeyOf={o => o.ownerName} dateOf={o => o.createdat} onRowClick={pick} activeRow={empSel} />
    <PivotMatrix title="Term Sheets Issued (by issued month)" items={issuedAll} rowKeyOf={o => o.ownerName} dateOf={o => o.term_sheet_issued_date} onRowClick={pick} activeRow={empSel} />
    <PivotMatrix title="Term Sheets Signed (by signed month)" items={signedAll} rowKeyOf={o => o.ownerName} dateOf={o => o.term_sheet_signed_date} onRowClick={pick} activeRow={empSel} />
  </>;
};
